import { memo, useRef } from 'react';

import { PartitionOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';

import { UnifiedCardTransaction } from '~apis/card';
import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import { CategoryTreeItem } from '~apis/categories';
import { LabeledCardBilling } from '~components/ui/AGGrid/editors/AGGridTransactionLabelSelectEditor';
import { FlattenTreeResult, flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { CashFlowType } from '~models/cashFlow';

import CategoryTreeSelect from './CategoryTreeSelect';
import CategoryLabelManagementModal from './LabelManagementModal';

interface Props {
  cashFlowType: CashFlowType;
  defaultActiveKey?: string;
  onSelect?: (categoryLabelKey: string, key: string[]) => void;
  onClear?: () => void;
  transaction: LabeledBankAccountTransaction | LabeledCardBilling | UnifiedCardTransaction;
  targetValue: FlattenTreeResult<CategoryTreeItem> | undefined;
  transactionId?: number;
  accountId?: number;
  triggerElementNode?: React.ReactNode;
  defaultShow?: boolean;
  filterCardLabel?: boolean;
  additionalFooterElement?: React.ReactNode;
  onSplitClick?: () => void;
}

// Type guard function
function isBankAccountTransaction(
  transaction: LabeledBankAccountTransaction | LabeledCardBilling | UnifiedCardTransaction,
): transaction is LabeledBankAccountTransaction {
  return !('paymentAmount' in transaction) && !('cardNo' in transaction);
}

interface DetailCategoryAddButtonProps {
  onClick?: () => void;
}

export const DetailCategoryAddButton: React.FC<DetailCategoryAddButtonProps> = ({ onClick }) => {
  return (
    <Button
      buttonType="primary"
      border
      style={{
        width: '100%',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
      onClick={() => {
        onClick?.();
        overlays.open(({ isOpen, onClose }) => (
          <CategoryLabelManagementModal open={isOpen} onCancel={onClose} />
        ));
      }}
    >
      + 계정 추가
    </Button>
  );
};

interface TransactionSplitButtonProps {
  onClick?: () => void;
}

const TransactionSplitButton: React.FC<TransactionSplitButtonProps> = ({ onClick }) => {
  return (
    <Button
      border
      style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
      icon={<PartitionOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      거래내역 분할
    </Button>
  );
};

const CategoryLabelTreeSelect: React.FC<Props> = ({
  cashFlowType,
  transactionId,
  triggerElementNode,
  accountId,
  onSelect,
  transaction,
  targetValue,
  onClear,
  defaultActiveKey,
  defaultShow = true,
  filterCardLabel = false,
  onSplitClick,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const panelRef = useRef<HTMLDivElement>(null);
  const { data: categoriesData } = useCategoriesTree({ companyId });
  const categories = categoriesData?.Data?.data?.tree?.children.filter((item) =>
    filterCardLabel ? !item.value?.isCardPayment : true,
  );

  const flattenedTree = flattenTree(categories ?? []).filter(
    (item) => !!item.value?.name && item.value?.cashFlowType === cashFlowType,
  );

  return (
    <CategoryTreeSelect
      disableScroll
      treeRef={panelRef}
      cashFlowType={cashFlowType}
      transactionId={transactionId}
      accountId={accountId}
      defaultActiveKey={defaultActiveKey ? [defaultActiveKey] : []}
      treeList={flattenedTree}
      onClear={onClear}
      triggerElement={
        triggerElementNode || (
          <div
            style={{
              height: '100%',
            }}
          />
        )
      }
      defaultShow={defaultShow}
      closeOnSelect
      onSelect={(categoryLabelKey, key) => {
        onSelect && onSelect(categoryLabelKey, key);
      }}
      footerElement={
        <div>
          {transaction && isBankAccountTransaction(transaction) ? (
            <Flex direction="column" gap={8} style={{ marginTop: 8 }}>
              <Flex align="center" gap={8}>
                <DetailCategoryAddButton
                  onClick={() => {
                    onClear?.();
                  }}
                />
                <TransactionSplitButton
                  onClick={() => {
                    onSplitClick?.();
                    onClear?.();
                  }}
                />
              </Flex>
            </Flex>
          ) : (
            <Flex direction="column" gap={8} style={{ marginTop: 8 }}>
              <Flex align="center" gap={8}>
                <DetailCategoryAddButton
                  onClick={() => {
                    onClear?.();
                  }}
                />
              </Flex>
            </Flex>
          )}
        </div>
      }
    />
  );
};

export default memo(CategoryLabelTreeSelect);
